import { ClientSideSuspense } from '@liveblocks/react/suspense'
import type { Appointment, Practitioner, Reference } from '@medplum/fhirtypes'
import { codeFromCodeableConcept } from '@seen/core/codeableConcept'
import { referenceDisplay } from '@seen/core/display'
import { featureFlags } from '@seen/core/featureFlags'
import { invariant } from '@seen/core/invariant'
import * as appointments from '@seen/core/models/appointments/index'
import { pendingDecisionCount } from '@seen/core/models/compositionNote'
import { ExtendedEncounter } from '@seen/core/models/encounters/encounter'
import { NoteLabel } from '@seen/core/models/noteLabel'
import type { NoteTemplate } from '@seen/core/models/noteTemplate'
import { ExtendedOrganization } from '@seen/core/models/organizations/ExtendedOrganization'
import { ParticipantNote } from '@seen/core/models/participantNote'
import { Participant } from '@seen/core/models/patients/participant'
import { VisitNote } from '@seen/core/models/visitNote'
import { isVisitNoteCategoryToken } from '@seen/core/models/visitNoteCategory'
import { parseReferenceString } from '@seen/core/utils'
import {
  Badge,
  Button,
  DotSeparator,
  IconCalendar,
  Item,
  ItemContent,
  ItemMedia,
  ItemTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  TypographySubtle,
} from '@seen/ui'
import {
  IconDots,
  IconHistory,
  IconLayoutSidebarRightCollapse,
  IconLayoutSidebarRightExpand,
  IconLock,
  IconLockOpen,
  IconPrinter,
  IconRefresh,
} from '@tabler/icons-react'
import {
  type DefaultError,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import type { Editor, JSONContent } from '@tiptap/react'
import { format, isSameDay, parse, parseISO, startOfDay } from 'date-fns'
import { sortBy, uniq } from 'lodash-es'
import {
  Fragment,
  type JSX,
  type MutableRefObject,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useNavigate, useParams } from 'react-router'
import { toast } from 'sonner'
import { CompositionActivity } from '#src/components/activity/CompositionActivity.tsx'
import { AppointmentDetails } from '#src/components/appointment/AppointmentDetails.tsx'
import {
  CollaborativeEditor,
  type DeferredRevealProps,
} from '#src/components/CollaborativeEditor.tsx'
import { Avatar, Sheet, SheetContent } from '#src/components/core/index.ts'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { DateTimeDisplay } from '#src/components/display/DateTimeDisplay.tsx'
import {
  DatePicker,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuDeleteItem,
  DropdownMenuElationLinkItem,
  DropdownMenuItem,
  DropdownMenuMedplumLinkItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  HoverDate,
  Loader,
  MoreDropdownMenu,
  ResourceFieldForm,
  RichText,
  RichTextEditor,
  ScrollArea,
  VersionHistoryDialog,
} from '#src/components/index.ts'
import { PractitionerNameplate } from '#src/components/nameplates/PractitionerNameplate.tsx'
import { NoteLabelComboboxButton } from '#src/components/NoteLabelComboboxButton.tsx'
import { OwnerRequiredComboboxButton } from '#src/components/OwnerRequiredComboboxButton.tsx'
import { runReconcileSweep } from '#src/components/rich-text-editor/acceptSuggestionPipeline.ts'
import { RequiredAnswersDialog } from '#src/components/rich-text-editor/required/RequiredAnswersDialog.tsx'
import { useRequiredAnswers } from '#src/components/rich-text-editor/required/useRequiredAnswers.ts'
import { StagedSuggestionsBanner } from '#src/components/rich-text-editor/StagedSuggestionsBanner.tsx'
import { CompositionContext } from '#src/contexts/CompositionContext.tsx'
import { CurrentParticipantContext, useSeenApi } from '#src/contexts/index.ts'
import { LayoutModeContext } from '#src/contexts/LayoutModeContext.ts'
import { usePatchMutation, useRead, useSearchOne } from '#src/hooks/index.ts'
import { useAIAssistantStore } from '#src/hooks/useAIAssistantStore.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useDeferredSuggestionReveal } from '#src/hooks/useDeferredSuggestionReveal.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useNavigationGuard } from '#src/hooks/useNavigationGuard.ts'
import { useParticipantNoteTitle } from '#src/hooks/useParticipantNoteTitle.ts'
import { usePrintWithDocumentTitle } from '#src/hooks/usePrintWithDocumentTitle.ts'
import type { SignVisitNoteResponse } from '#src/hooks/useSignParticipantNotes.ts'
import {
  type SuggestionPersistence,
  useSuggestionPersistence,
} from '#src/hooks/useSuggestionPersistence.ts'
import { AppointmentLink } from '#src/pages/participants/timeline/AppointmentLink.tsx'
import { VisitNoteTranscriptView } from '#src/pages/participants/timeline/VisitNoteTranscriptView.tsx'
import { VisitNoteBillingWidget } from '#src/pages/participants/visit-notes/VisitNoteBillingWidget.tsx'
import { VisitNoteConditionsWidget } from '#src/pages/participants/visit-notes/VisitNoteConditionsWidget.tsx'
import { VisitNoteDocumentsWidget } from '#src/pages/participants/visit-notes/VisitNoteDocumentsWidget.tsx'
import { VisitNoteMedicationsWidget } from '#src/pages/participants/visit-notes/VisitNoteMedicationsWidget.tsx'
import { VisitNoteOrdersWidget } from '#src/pages/participants/visit-notes/VisitNoteOrdersWidget.tsx'
import { VisitNotePrintHeader } from '#src/pages/participants/visit-notes/VisitNotePrintHeader.tsx'
import { VisitNoteVitalsWidget } from '#src/pages/participants/visit-notes/VisitNoteVitalsWidget.tsx'
import { contentToHTML, parseNoteContent } from '#src/utils/noteContent.ts'

// onDelete replaces leaving the page once its note is deleted (an embedding
// host closes its own view).
export function ParticipantVisitNotePage({
  onDelete,
}: {
  onDelete?: () => void
}): JSX.Element {
  const { patientId, visitNoteId } = useParams()
  invariant(patientId, 'Patient ID is required')
  invariant(visitNoteId, 'Visit note ID is required')
  return (
    <ParticipantVisitNotePageComponent
      patientId={patientId}
      visitNoteId={visitNoteId}
      onDelete={onDelete}
    />
  )
}

function ParticipantVisitNotePageComponent({
  patientId,
  visitNoteId,
  onDelete,
}: {
  patientId: string
  visitNoteId: string
  onDelete?: () => void
}): JSX.Element {
  const { data, refetch, isLoading } = useRead(ParticipantNote, visitNoteId)

  const elationVisitNote = useMemo(() => {
    const fhirResource = data?.toFhirResource()
    if (VisitNote.isInstance(fhirResource)) {
      return VisitNote.fromFhirResource(fhirResource)
    }
    return undefined
  }, [data])

  if (elationVisitNote) {
    return (
      <ElationVisitNotePageComponent
        patientId={patientId}
        visitNote={elationVisitNote}
        onSync={refetch}
      />
    )
  }

  if (!data && !isLoading) {
    // TODO: standardize 404
    return <h2>Note not found</h2>
  }

  return (
    <ParticipantNotePageComponent
      note={data}
      patientId={patientId}
      onDelete={onDelete}
    />
  )
}

function ParticipantNotePageComponent({
  note,
  patientId,
  onDelete,
}: {
  note: ParticipantNote | undefined
  patientId: string
  onDelete?: () => void
}): JSX.Element {
  const { data: participant } = useRead(Participant, note?.participant)
  // Seen Health org supplies the print letterhead (name + phone/fax). Optional —
  // the print header omits the letterhead line if it hasn't loaded.
  const { data: organization } = useSearchOne(
    ExtendedOrganization,
    ExtendedOrganization.querySeenHealthOrg()
  )
  const isReadOnly = useIsReadOnly()
  const currentPractitioner = useCurrentPractitioner()
  const setResourceContext = useAIAssistantStore(
    (state) => state.setResourceContext
  )
  const clearResourceContext = useAIAssistantStore(
    (state) => state.clearResourceContext
  )
  const [unlocked, setUnlocked] = useState(false)

  const setLayoutMode = useContext(LayoutModeContext)
  useLayoutEffect(() => {
    setLayoutMode?.('full')
    return () => setLayoutMode?.('auto')
  }, [setLayoutMode])
  const [isVersionHistoryDialogOpen, setIsVersionHistoryDialogOpen] =
    useState(false)
  const editorRef = useRef<Editor | null>(null)
  const flushPendingSaveRef = useRef<(() => Promise<void>) | null>(null)

  const { mutate: patchNote, mutateAsync } = usePatchMutation<ParticipantNote>()
  const title = useParticipantNoteTitle(note)
  // Descriptive default filename for print / "Save as PDF" — matches the
  // identity + date shown in the print header. Any missing part is dropped.
  const printFilename = useMemo(
    () =>
      [
        participant?.name,
        title,
        note?.created
          ? format(parseISO(note.created), 'yyyy-MM-dd')
          : undefined,
      ]
        .filter(Boolean)
        .join(' - '),
    [participant?.name, title, note?.created]
  )
  const print = usePrintWithDocumentTitle(printFilename)
  const navigate = useNavigate()

  const openVersionHistoryDialog = useCallback(() => {
    setIsVersionHistoryDialogOpen(true)
  }, [])

  const handleRestoreVersion = useCallback(
    (version: ParticipantNote) => {
      if (!note) {
        return
      }

      patchNote({
        instance: note,
        mutateFn: (n) => {
          n.content = version.content
          n.attester = undefined
        },
        patchOptions: {
          skipVersionCheck: true,
        },
      })
      // Liveblocks' room storage is the source of truth while the editor is
      // mounted; `CollaborativeEditor`'s `onUpdate` keydown gate prevents
      // programmatic `setContent` from triggering its own save path, so we
      // also patch Medplum directly. Write into the live editor too — without
      // it, the stale room content stays on screen and clobbers the restore
      // on the next debounce flush. y-prosemirror propagates this into the
      // Y.Doc for collaborators.
      // TODO(follow-up PR): swap the keydown gate for a y-prosemirror
      // y-sync-meta filter so `setContent` triggers persistence itself and
      // this direct patch becomes redundant.
      editorRef.current?.commands.setContent(parseNoteContent(version.content))
      toast.success('Version restored')
    },
    [note, patchNote]
  )

  /* Union an @-mention-inserted template's labels + categories into the note.
   * Passed down to the editor's mention extension (via `onMergeTemplateMetadata`)
   * rather than done in the editor so every label/category write goes through the
   * same metadata-only patch as the inline picker: it touches neither `content`
   * nor `attester`, so merging into a signed note can't unsign it.
   * `skipVersionCheck` matches the picker, avoiding a 412 against the
   * collaborative editor's content autosave. */
  const mergeTemplateMetadata = useCallback(
    (template: NoteTemplate) => {
      if (!note) {
        return
      }
      patchNote({
        instance: note,
        mutateFn: (n) => {
          n.labels = uniq([...n.labels, ...template.labels])
          n.categories = uniq([...n.categories, ...template.categories])
        },
        patchOptions: { skipVersionCheck: true },
      })
    },
    [note, patchNote]
  )

  const noteId = note?.id ?? null
  useEffect(() => {
    if (noteId) {
      setResourceContext(`Composition/${noteId}`)
    }
  }, [noteId, setResourceContext])

  /* Cleared on unmount and on a participant change — separate effect so
   * navigating between notes for the SAME participant doesn't briefly null out
   * the context. The participant dependency is load-bearing: this route stays
   * mounted across a participant switch and the effect above only republishes
   * once the new note resolves, so without it the assistant would keep the
   * previous participant's `Composition` ref while the new note loads. */
  // patientId is a reset key — the cleanup must rerun when the participant changes even though it never reads it.
  useEffect(() => {
    return () => {
      clearResourceContext()
    }
  }, [patientId, clearResourceContext])

  /* Staged-suggestions banner, hoisted OUT of the editor's `<ClientSideSuspense>`
   * boundary (#10). The per-practitioner staging read and the per-tab reveal
   * lifecycle the banner needs are editor-independent, so running them here lets
   * the banner paint as soon as the (parallel) staging search settles instead of
   * waiting for Liveblocks to connect + sync the editor (the ~3s delay). The
   * editor still drives the actual paint via the threaded-down reveal state.
   * `useSuggestionPersistence` here subscribes to the SAME author-scoped staging
   * query key the editor's own call uses, so React Query dedups the request.
   * Signed notes keep their staging record (don't-delete policy) but never show
   * the banner or mount the collaborative editor, so skip the fetch. */
  const suggestionPersistence = useSuggestionPersistence(
    note?.isSigned || !note?.id ? undefined : `Composition/${note.id}`
  )
  const getEditor = useCallback(() => editorRef.current, [])
  const {
    revealed,
    revealing,
    revealGen,
    handleReveal,
    handleRevealSettled,
    handleLiveStaged,
    handleHide,
  } = useDeferredSuggestionReveal({
    // This hook outlives the (resource-keyed) editor across a note switch, so
    // it must re-latch per resource — see the hook's `resourceKey` docs.
    resourceKey: note?.id ? `Composition/${note.id}` : undefined,
    isResourceLoaded: suggestionPersistence.isResourceLoaded,
    hasPersistedPending: suggestionPersistence.hasPersistedPending,
    getEditor,
  })
  const deferredReveal = useMemo<DeferredRevealProps>(
    () => ({
      revealed,
      revealing,
      revealGen,
      onRevealSettled: handleRevealSettled,
      onHide: handleHide,
      onLiveStaged: handleLiveStaged,
    }),
    [
      revealed,
      revealing,
      revealGen,
      handleRevealSettled,
      handleHide,
      handleLiveStaged,
    ]
  )

  /* Guard against leaving while an AI generation is actively streaming/staging
   * edits into this editor (#3). Navigating away aborts the run, silently
   * losing the in-progress suggestions. `isGenerating` is mirrored into the
   * global assistant store by the chat provider (the chat's own streaming state
   * is scoped to the sibling assistant panel). Scope the guard to:
   *   - the editable editor (a signed/read-only note isn't accepting staged
   *     edits, so there's nothing in flight to lose), AND
   *   - a generation whose assistant resource context IS this note —
   *     `assistantResourceRef` is set to `Composition/${note.id}` by the page's
   *     `setResourceContext` effect, so a patient-level assistant run (no
   *     resource context, or a different resource) does NOT warn that leaving
   *     will lose THIS note's suggestions. */
  const isGenerating = useAIAssistantStore((state) => state.isGenerating)
  const assistantResourceRef = useAIAssistantStore((state) => state.resourceRef)
  const isEditorEditable = !!note && (!note.isSigned || unlocked)
  useNavigationGuard(
    isGenerating &&
      isEditorEditable &&
      assistantResourceRef === (note?.id ? `Composition/${note.id}` : null),
    'Orbit AI is still generating suggestions for this note. Leaving now will stop it and lose the suggestions in progress. Leave anyway?'
  )

  if (!note) {
    return <Loader />
  }

  const editable = !note.isSigned || unlocked
  const canUnlockNote =
    !note.signerReference ||
    currentPractitioner.reference.reference === note.signerReference.reference

  const readOnlyEditor = (
    <RichTextEditor
      value={note.content}
      variant="unstyled"
      enableColumns
      editable={false}
      participant={participant}
    />
  )

  /* Note labels and categories are metadata, not signed clinical content, so
   * this control stays interactive even on a signed note. The combobox value is
   * the union of label references (persisted to `meta.tag`) and category tokens
   * (persisted to `Composition.category[]`); `patchFn` splits them back to their
   * own fields. Neither write touches `content` or clears `attester`, so a
   * change here can't unsign the note. `skipVersionCheck` avoids a 412 racing
   * the collaborative editor's content autosave (which skips its own check). */
  const inlineLabelControl = (
    <ResourceFieldForm
      instance={note}
      valueFn={(n) => [...n.labels, ...n.categories]}
      patchFn={(n, value) => {
        const next = value as string[]
        n.labels = next.filter(NoteLabel.isValidReferenceString)
        n.categories = next.filter(isVisitNoteCategoryToken)
      }}
      skipVersionCheck
      render={({ field }) => (
        <NoteLabelComboboxButton {...field} includeCategories />
      )}
    />
  )

  return (
    <div className="flex h-full flex-col border-t print:border-t-0">
      <div className="flex items-center justify-between border-b px-pagex-default py-2.5 lg:px-pagex-lg print:hidden">
        {editable && (
          <div className="flex flex-wrap items-center gap-2">
            <ResourceFieldForm
              instance={note}
              valueFn={(n) => n.created}
              patchFn={(n, value) => {
                n.created = value
              }}
              render={({ field: { onChange, ...rest } }) => (
                <DatePicker
                  onChange={(d) => {
                    invariant(d, 'Date is required')
                    onChange(d)
                  }}
                  {...rest}
                  className="h-10"
                  clearText={null}
                />
              )}
            />
            <ResourceFieldForm
              instance={note}
              valueFn={(n) => n.owner.reference ?? null}
              patchFn={(n, owner) => {
                const ownerReference = parseReferenceString<Practitioner>(owner)
                invariant(ownerReference, 'Owner is required')
                n.owner = ownerReference
                n.attester = undefined
              }}
              render={({ field }) => <OwnerRequiredComboboxButton {...field} />}
            />
            {inlineLabelControl}
          </div>
        )}
        {!editable && (
          <div className="flex flex-wrap items-center gap-2 py-2.5">
            <div className="flex items-center gap-2 border-x border-transparent px-4 text-sm">
              <IconCalendar />
              <HoverDate date={note.created} tooltipPrefix="Created on" />
            </div>
            <div className="flex items-center border-x border-transparent px-4 text-sm">
              <PractitionerNameplate
                practitioner={note.owner.reference}
                size={5}
              />
            </div>
            {note.signedOn && (
              <div
                data-testid="visit-note-signed-chip"
                className="flex items-center gap-1 border-x border-transparent px-4 text-sm text-foreground-secondary"
              >
                Signed
                {note.signerReference && (
                  <>
                    {' by '}
                    <PractitionerNameplate
                      practitioner={note.signerReference}
                      size={5}
                    />
                  </>
                )}
                <HoverDate
                  date={note.signedOn}
                  tooltipPrefix={
                    referenceDisplay(note.signerReference)
                      ? `Signed by ${referenceDisplay(note.signerReference)} on`
                      : 'Signed on'
                  }
                />
              </div>
            )}
            {inlineLabelControl}
          </div>
        )}
        <div className="flex items-center gap-2">
          {!isReadOnly && !note.isSigned && (
            <SignButton
              note={note}
              patientId={patientId}
              flushPendingSaveRef={flushPendingSaveRef}
              editorRef={editorRef}
              suggestionPersistence={suggestionPersistence}
            />
          )}
          {!isReadOnly &&
            note.isSigned &&
            (canUnlockNote ? (
              <Button
                variant="outline"
                onClick={() => setUnlocked((locked) => !locked)}
                size="icon"
              >
                {unlocked ? (
                  <IconLockOpen className="size-4" />
                ) : (
                  <IconLock className="size-4" />
                )}
              </Button>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <Button variant="outline" disabled size="icon">
                      <IconLock className="size-4" />
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom" align="center">
                  Only the practitioner who signed this note can unlock it.
                </TooltipContent>
              </Tooltip>
            ))}
          <MoreDropdownMenu>
            <DropdownMenuItem onSelect={print}>
              <IconPrinter className="size-4" />
              Print
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={openVersionHistoryDialog}>
              <IconHistory className="size-4" />
              Version history
            </DropdownMenuItem>
            {!isReadOnly && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuDeleteItem
                  deleteConfirmationProps={{
                    title: `Delete "${title}"?`,
                    description:
                      'Deleted notes can still be recovered by contacting our team.',
                    onConfirm: () => {
                      if (onDelete) {
                        onDelete()
                      } else {
                        void navigate(-1)
                      }
                      mutateAsync({
                        instance: note,
                        mutateFn: (t) => {
                          t.status = 'entered-in-error'
                        },
                      })
                        .then(() => toast.success(`"${title}" deleted`))
                        .catch((error: DefaultError) => {
                          toast.error(`Failed to delete "${title}": ${error}`)
                        })
                    },
                  }}
                />
              </>
            )}
            <DropdownMenuMedplumLinkItem to={note} />
          </MoreDropdownMenu>
        </div>
      </div>
      <VersionHistoryDialog
        open={isVersionHistoryDialogOpen}
        onOpenChange={setIsVersionHistoryDialogOpen}
        resource={note}
        onRestoreVersion={handleRestoreVersion}
      />
      <ScrollArea className="flex-1" scrollRestorationId={null}>
        <CurrentParticipantContext.Provider value={participant ?? null}>
          <CompositionContext.Provider value={note.reference.reference}>
            <VisitNotePrintHeader
              note={note}
              participant={participant}
              organization={organization}
            />
            <div
              data-testid="visit-note-editor"
              // Pin the body font size onto `.ProseMirror` itself rather than the
              // wrapper. The read-only RichTextEditor's `unstyled` variant already
              // puts `text-sm` directly on its own `.ProseMirror`; the editable
              // CollaborativeEditor's `.ProseMirror` carries no font-size class and
              // otherwise falls back to the 16px document base. Targeting the
              // editor element directly matches the read-only mechanism so signed
              // and unsigned notes both render at 14px without depending on cascade.
              className="px-pagex-default pt-6 pb-10 lg:px-pagex-lg print:px-0 [&_.ProseMirror]:text-sm [&_.ProseMirror>*]:max-w-(--container-prose) [&_.ProseMirror>:has([data-layout])]:max-w-none"
            >
              {editable ? (
                <div className="flex flex-col gap-2">
                  {/* Rendered OUTSIDE the suspense boundary (#10) so it paints as
                   * soon as the parallel staging search settles, not after the
                   * editor boots. Gated identically to the editor's former
                   * internal banner: pending staging exists and this tab hasn't
                   * revealed it. It also stays mounted while `revealing` — the
                   * paint waits on the editor's collab sync, which this surface
                   * does not wait for, so the click needs somewhere to land. */}
                  {suggestionPersistence.isResourceLoaded &&
                    suggestionPersistence.hasPersistedPending &&
                    (!revealed || revealing) && (
                      // print:hidden — the AI staged-suggestions banner is
                      // interactive AI chrome, not clinical content.
                      <div className="print:hidden">
                        {/* Undecided units, not records, so this deferred count
                         * matches the status bar's post-reveal one: a Braden
                         * with two subscales still open is 2, not 1. */}
                        <StagedSuggestionsBanner
                          count={pendingDecisionCount(
                            suggestionPersistence.pendingSuggestions
                          )}
                          onReview={handleReveal}
                          revealing={revealing}
                        />
                      </div>
                    )}
                  <ClientSideSuspense fallback={readOnlyEditor}>
                    <VisitNoteBodyCollaborativeEditor
                      key={note.id}
                      note={note}
                      participant={participant}
                      editorRef={editorRef}
                      flushPendingSaveRef={flushPendingSaveRef}
                      deferredReveal={deferredReveal}
                      onMergeTemplateMetadata={mergeTemplateMetadata}
                    />
                  </ClientSideSuspense>
                </div>
              ) : (
                readOnlyEditor
              )}
            </div>
            <div className="px-pagex-default pb-16 lg:px-pagex-lg print:hidden">
              <CompositionActivity composition={note} resourceNoun="note" />
            </div>
          </CompositionContext.Provider>
        </CurrentParticipantContext.Provider>
      </ScrollArea>
    </div>
  )
}

function VisitNoteBodyCollaborativeEditor({
  note,
  participant,
  editorRef,
  flushPendingSaveRef,
  deferredReveal,
  onMergeTemplateMetadata,
}: {
  note: ParticipantNote
  /* Optional because the parent's `useRead(Participant, …)` can briefly return
   * `undefined` while the fetch resolves. `CollaborativeEditor`'s
   * `useTiptapExtensions({ contextParticipant })` accepts undefined; the
   * encounter-medications-block then shows its empty state until participant
   * loads, which matches the page's existing behavior for participant-derived
   * affordances elsewhere. */
  participant: Participant | undefined
  editorRef: MutableRefObject<Editor | null>
  flushPendingSaveRef: MutableRefObject<(() => Promise<void>) | null>
  /* Reveal state owned by the page (banner lives outside this suspense
   * boundary, #10). Threaded straight through to the editor. */
  deferredReveal: DeferredRevealProps
  /* Merges an @-mention-inserted template's labels + categories into the note.
   * Owned by the page (which holds the single label/category write path) and
   * forwarded to the mention extension's options. */
  onMergeTemplateMetadata: (template: NoteTemplate) => void
}): ReactNode {
  const { mutateAsync: patchNoteAsync } = usePatchMutation<ParticipantNote>()
  // Read only to resolve registry variables bound to the encounter.
  const { data: encounterModel } = useRead(ExtendedEncounter, note.encounter)
  const encounter = useMemo(
    () => encounterModel?.toFhirResource(),
    [encounterModel]
  )
  const handleChange = useCallback(
    (content: string) => {
      const promise = patchNoteAsync({
        instance: note,
        mutateFn: (n) => {
          n.content = content
          n.attester = undefined
        },
        patchOptions: { skipVersionCheck: true },
      })
      // Silence unhandled-rejection from fire-and-forget autosave; awaiters still see errors.
      promise.catch(() => {})
      return promise
    },
    [patchNoteAsync, note]
  )
  // Diagnostics only. `note.versionId` throws on a resource with no version,
  // and an observer must never be able to take the page down with it.
  const contentVersionId = useMemo(
    () => note.toFhirResource().meta?.versionId,
    [note]
  )
  const aiMetadata = useMemo(
    () => ({
      id: `visit-note-${note.id}`,
      label: 'Visit note',
      resourceRef: note.reference.reference,
      field: note.fragmentName,
      description:
        "Free-text narrative body of the participant's PACE visit note. Records the clinician's assessment, plan, and encounter detail. Avoid touching custom widgets (validated tools, encounters, signature buttons); prefer scoped anchored edits over replaceAll.",
    }),
    [note.id, note.fragmentName, note.reference.reference]
  )
  return (
    <CollaborativeEditor
      field={note.fragmentName}
      contentVersionId={contentVersionId}
      initialContent={contentToHTML(note.content)}
      onChange={handleChange}
      placeholder="Write, press '@' for commands…"
      editorRef={editorRef}
      flushPendingSaveRef={flushPendingSaveRef}
      outputFormat="json"
      participant={participant}
      encounter={encounter}
      aiMetadata={aiMetadata}
      deferredReveal={deferredReveal}
      onMergeTemplateMetadata={onMergeTemplateMetadata}
      observationRecordStatus="deferred"
    />
  )
}

function SignButton({
  note,
  patientId,
  flushPendingSaveRef,
  editorRef,
  suggestionPersistence,
}: {
  note: ParticipantNote
  patientId: string
  flushPendingSaveRef: MutableRefObject<(() => Promise<void>) | null>
  editorRef: MutableRefObject<Editor | null>
  suggestionPersistence: SuggestionPersistence
}): JSX.Element {
  const { seenApi } = useSeenApi()
  const medplum = useMedplum()
  const queryClient = useQueryClient()
  const noteSuggestionsEnabled = useFeatureFlag(featureFlags.aiNoteSuggestions)
  const citationMentionsEnabled = useFeatureFlag(
    featureFlags.aiCitationMentions
  )

  const getEditor = useCallback(() => editorRef.current, [editorRef])
  const required = useRequiredAnswers({
    getEditor,
    surface: 'visit-note',
    action: 'Sign',
  })

  const signMutation = useMutation({
    mutationFn: async (): Promise<'signed' | 'cancelled'> => {
      // Pre-sign reconcile drain (Option C Card 3): sweep any just-created orphan
      // or draft-flagged condition linkage to convergence before signing. Signing
      // proceeds UNCONDITIONALLY — Option C removed the hard AI-suggestion sign
      // gate; reveal-time + accept-time validation now prevent the bad states and
      // this drain heals the residual transient tail — so a sweep failure must
      // never block the sign (best-effort, swallowed).
      const editor = editorRef.current
      if (editor) {
        try {
          await runReconcileSweep({
            editor,
            persistence: suggestionPersistence,
            medplum,
            resourceRef: `Composition/${note.id}`,
            patientRef: `Patient/${patientId}`,
            noteSuggestionsEnabled,
            citationMentionsEnabled,
            onDropped: () => {},
          })
        } catch {
          // Best-effort: never block signing on a reconcile failure.
        }
      }
      // Required questions the note shows must be answered, unless the
      // clinician signs anyway.
      const before = await required.check()
      if (before === 'answer') {
        return 'cancelled'
      }
      // Drain a pending body edit so the server signs the fresh content.
      await flushPendingSaveRef.current?.()
      const sign = async (signAnyway: boolean) =>
        (
          await seenApi.post<SignVisitNoteResponse>('/sign-visit-note', {
            patientId,
            noteId: note.id,
            signAnyway,
          })
        ).data
      const result = await sign(before === 'override')
      if (!result || result.success) {
        return 'signed'
      }
      // The server reads the note as saved, so it can find a gap the editor
      // had not settled yet.
      if ((await required.check(result.requiredGaps)) !== 'override') {
        return 'cancelled'
      }
      await sign(true)
      return 'signed'
    },
    onSuccess: (outcome) => {
      if (outcome === 'cancelled') {
        return
      }
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: ['Composition', note.id] }),
        queryClient.invalidateQueries({ queryKey: ['Condition'] }),
        queryClient.invalidateQueries({ queryKey: ['AllergyIntolerance'] }),
        queryClient.invalidateQueries({ queryKey: ['Observation'] }),
      ])
      toast.success('Note signed')
    },
    onError: (error) => {
      toast.error(`Failed to sign note: ${error}`)
    },
  })

  return (
    <>
      <Button
        loading={signMutation.isPending}
        onClick={() => signMutation.mutate()}
      >
        Sign
      </Button>
      <RequiredAnswersDialog {...required.dialog} />
    </>
  )
}

function ElationVisitNotePageComponent({
  patientId,
  visitNote,
  onSync,
}: {
  patientId: string
  visitNote: VisitNote
  onSync: () => void
}): JSX.Element {
  const setLayoutMode = useContext(LayoutModeContext)
  useLayoutEffect(() => {
    setLayoutMode?.('full')
    return () => setLayoutMode?.('auto')
  }, [setLayoutMode])

  const { data: participant } = useRead(Participant, patientId)
  const appointmentQuery = useSearchOne(appointments.ExtendedAppointment, {
    '_has:Encounter:appointment:_id': visitNote?.encounter,
  })

  const { seenApi } = useSeenApi()
  const syncVisitNoteMutation = useMutation<
    unknown,
    unknown,
    { elationVisitNoteId: string }
  >({
    mutationFn({ elationVisitNoteId }) {
      return seenApi.post('/sync-visit-note', {
        elationVisitNoteId,
      })
    },
    onSuccess: onSync,
  })

  const elationVisitNoteId = visitNote.elationId?.toString()
  const [selectedAppointment, setSelectedAppointment] =
    useState<Reference<Appointment> | null>(null)
  const [sidebarOpen, setSidebarOpen] = useState(true)

  const { sections } = visitNote

  const newFormatContent = useMemo<JSONContent | null>(() => {
    const contentSection = Object.values(sections)
      .flat()
      .find((s) => VisitNote.isContentSection(s))
    if (!contentSection?.text?.div) {
      return null
    }
    try {
      return JSON.parse(contentSection.text.div) as JSONContent
    } catch (e) {
      console.error('Failed to parse visit note content', e)
      return null
    }
  }, [sections])

  const isNewFormat = newFormatContent !== null

  useEffect(() => {
    if (
      !newFormatContent &&
      Object.values(sections)
        .flat()
        .some((s) => VisitNote.isContentSection(s))
    ) {
      toast.error('Failed to load visit note content')
    }
  }, [newFormatContent, sections])

  const hasLinkedEntities = useMemo(
    () =>
      Object.values(sections)
        .flat()
        .some(
          (s) =>
            (VisitNote.isOrderSection(s) ||
              VisitNote.isConditionSection(s) ||
              VisitNote.isObservationSection(s) ||
              VisitNote.isDocumentSection(s)) &&
            (s.entry?.length ?? 0) > 0
        ),
    [sections]
  )

  const contentSections = Object.entries(sections).filter(
    ([_, s]) =>
      !VisitNote.isOrderSection(s[0]) && !VisitNote.isConditionSection(s[0])
  )
  const hasSections = Object.keys(sections).length > 0
  const hasContentSections = contentSections.length > 0

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-none flex-col gap-2 bg-frame p-4">
        <div className="flex justify-between">
          <h2 className="flex items-center gap-2 text-lg font-medium">
            {visitNote.displayTitle}
            {visitNote.elationId != null && (
              <Badge variant="outline" color="blue">
                Elation
              </Badge>
            )}
          </h2>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="p-0">
                <IconDots size={18} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {elationVisitNoteId && (
                <DropdownMenuItem
                  onSelect={() =>
                    syncVisitNoteMutation.mutate({
                      elationVisitNoteId,
                    })
                  }
                  disabled={syncVisitNoteMutation.isPending}
                >
                  <IconRefresh size={16} />
                  Refresh from Elation
                </DropdownMenuItem>
              )}
              {appointmentQuery.data && (
                <DropdownMenuItem asChild>
                  <AppointmentLink appointment={appointmentQuery.data}>
                    <IconCalendar size={16} />
                    View Appointment
                  </AppointmentLink>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              {participant?.elationId && (
                <DropdownMenuElationLinkItem
                  elationPatientId={participant.elationId}
                  elationFeedItemId={visitNote.elationId}
                />
              )}
              <DropdownMenuMedplumLinkItem to={visitNote.reference} />
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-foreground-secondary">
          <div className="flex items-center gap-1">
            <IconCalendar size={14} />
            <HoverDate
              date={
                appointmentQuery.data?.start ??
                visitNote.date ??
                visitNote.lastUpdated
              }
              tooltipPrefix={
                appointmentQuery.data?.start ? 'Appointment on' : 'Created on'
              }
            />
          </div>
          {visitNote.practitioner && (
            <PractitionerNameplate
              practitioner={visitNote.practitioner}
              size={5}
            />
          )}
          {visitNote.date &&
            appointmentQuery.data?.start &&
            !isSameDay(
              parse(visitNote.date.slice(0, 10), 'yyyy-MM-dd', new Date()),
              startOfDay(parseISO(appointmentQuery.data.start))
            ) && (
              <span className="flex items-center gap-1">
                Note created{' '}
                <HoverDate date={visitNote.date} tooltipPrefix="Created on" />
              </span>
            )}
          {visitNote.signedDate && (
            <span className="flex items-center gap-1">
              Signed
              {visitNote.signedBy && (
                <>
                  {' by '}
                  <PractitionerNameplate
                    practitioner={visitNote.signedBy}
                    size={5}
                  />
                </>
              )}
              <HoverDate
                date={visitNote.signedDate}
                tooltipPrefix={
                  referenceDisplay(visitNote.signedBy)
                    ? `Signed by ${referenceDisplay(visitNote.signedBy)} on`
                    : 'Signed on'
                }
              />
            </span>
          )}
        </div>
      </div>
      <Tabs defaultValue="visit-note" className="flex min-h-0 flex-1 flex-col">
        <TabsList
          variant="underline"
          className="w-full flex-none justify-start bg-frame [&>button[data-state=active][role=tab]]:bg-frame"
        >
          <TabsTrigger value="visit-note">Visit Note</TabsTrigger>
          <TabsTrigger value="transcript">Transcript</TabsTrigger>
        </TabsList>
        <div className="min-h-0 flex-1 overflow-hidden">
          <TabsContent value="visit-note" className="mt-0 h-full">
            <div className="flex h-full">
              <ScrollArea className="h-full flex-1" scrollRestorationId={null}>
                <div className="p-3">
                  {appointmentQuery.data && (
                    <div className="mb-3">
                      <Item variant="outline">
                        <ItemMedia>
                          <IconCalendar />
                        </ItemMedia>
                        <ItemContent>
                          <ItemTitle className="min-h-10">
                            <h2 className="flex items-center gap-1">
                              <Button
                                variant="link"
                                onClick={() => {
                                  if (appointmentQuery.data) {
                                    setSelectedAppointment(
                                      appointmentQuery.data.reference
                                    )
                                  }
                                }}
                              >
                                {appointmentQuery.data.getDisplay()}
                              </Button>
                              <DotSeparator />
                              <span className="text-foreground-secondary">
                                <DateTimeDisplay
                                  value={appointmentQuery.data.start}
                                />
                              </span>
                            </h2>
                            {appointmentQuery.data.practitioners.length > 0 && (
                              <Avatar
                                profile={appointmentQuery.data.practitioners[0]}
                                size="icon"
                              />
                            )}
                          </ItemTitle>
                        </ItemContent>
                      </Item>
                      <Sheet
                        open={!!selectedAppointment}
                        onOpenChange={(open) => {
                          if (!open) {
                            setSelectedAppointment(null)
                          }
                        }}
                      >
                        <SheetContent side="right" className="max-w-xl">
                          {selectedAppointment && (
                            <AppointmentDetails
                              appointmentRef={selectedAppointment}
                            />
                          )}
                        </SheetContent>
                      </Sheet>
                    </div>
                  )}
                  {/* TODO: Remove old-format branch after visit note backfill migration.
                      Once all Compositions use single-section TipTap JSON, delete the
                      !hasSections / contentSections / sortBy path below and render
                      newFormatContent unconditionally. */}
                  {isNewFormat ? (
                    newFormatContent ? (
                      <RichTextEditor
                        value={newFormatContent}
                        variant="unstyled"
                        contentClassName="pt-6 pb-16 px-pagex-default lg:px-pagex-lg *:max-w-(--container-prose) [&>:has([data-layout])]:max-w-none [&_h1]:border-b [&_h1]:border-border [&_h1]:pb-1 [&_h1]:mb-3 [&_h1:not(:first-child)]:mt-8 [&_h2:not(:first-child)]:mt-6 [&_li]:mb-1"
                        editable={false}
                        enableColumns
                      />
                    ) : (
                      <div className="p-3 text-destructive">
                        Unable to display note content
                      </div>
                    )
                  ) : !hasSections ? (
                    <TypographySubtle className="text-muted-foreground">
                      No contents found. If changes have been made and saved in
                      Elation,{' '}
                      <Button
                        variant="link"
                        className="h-auto p-0"
                        onClick={() =>
                          syncVisitNoteMutation.mutate({
                            elationVisitNoteId: elationVisitNoteId ?? '',
                          })
                        }
                        loading={syncVisitNoteMutation.isPending}
                      >
                        click here to refresh
                      </Button>
                      .
                    </TypographySubtle>
                  ) : !hasContentSections ? (
                    <TypographySubtle className="text-muted-foreground">
                      (No content)
                    </TypographySubtle>
                  ) : (
                    sortBy(contentSections, ([_, sectionList]) => {
                      const titleSection = sectionList[0]
                      const code = codeFromCodeableConcept(titleSection.code)

                      switch (code) {
                        case 'Problem':
                          return 0
                        case 'Assessplan':
                          return 2
                        case 'Assessment':
                          return 3
                        case 'Plan':
                          return 4
                        case 'Test':
                          return 5
                        case 'Tx':
                          return 6
                        case 'Instr':
                          return 7
                        case 'Followup':
                          return 8
                        default:
                          return 1
                      }
                    }).map(([title, sections]) => (
                      <div key={`${title}-${sections[0].id}`} className="mb-4">
                        <h3 className="text-xl font-medium">{title}</h3>
                        {sections.map((section) => (
                          <Fragment key={section.id}>
                            {!!section.text?.div?.trim() && (
                              <RichText
                                value={
                                  section.text?.div.replace(/\n/g, '<br />') ??
                                  ''
                                }
                                participant={participant}
                              />
                            )}
                            {section.section?.map((subSection) => (
                              <RichText
                                key={subSection.id}
                                value={
                                  subSection.text?.div.replace(
                                    /\n/g,
                                    '<br />'
                                  ) ?? ''
                                }
                                participant={participant}
                              />
                            ))}
                          </Fragment>
                        ))}
                      </div>
                    ))
                  )}
                  <VisitNoteBillingWidget visitNote={visitNote} />
                </div>
              </ScrollArea>
              {hasLinkedEntities && (
                <>
                  <div className="relative shrink-0 border-l border-border">
                    <button
                      type="button"
                      onClick={() => setSidebarOpen((o) => !o)}
                      className="absolute top-1/2 right-full z-10 mr-1 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-background text-muted-foreground shadow-sm transition-colors hover:bg-muted hover:text-foreground"
                      aria-label={
                        sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'
                      }
                    >
                      {sidebarOpen ? (
                        <IconLayoutSidebarRightCollapse size={14} />
                      ) : (
                        <IconLayoutSidebarRightExpand size={14} />
                      )}
                    </button>
                  </div>
                  {sidebarOpen && (
                    <div className="h-full w-72 shrink-0 overflow-y-auto">
                      <div className="space-y-3 p-3">
                        <VisitNoteVitalsWidget visitNote={visitNote} />
                        <VisitNoteMedicationsWidget
                          visitNote={visitNote}
                          patientId={patientId}
                        />
                        <VisitNoteOrdersWidget visitNote={visitNote} />
                        <VisitNoteConditionsWidget
                          visitNote={visitNote}
                          patientId={patientId}
                        />
                        <VisitNoteDocumentsWidget
                          visitNote={visitNote}
                          patientId={patientId}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </TabsContent>
          <TabsContent value="transcript" className="mt-0 h-full">
            <ScrollArea className="h-full p-4" scrollRestorationId={null}>
              <VisitNoteTranscriptView visitNote={visitNote} />
            </ScrollArea>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  )
}
